"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import PageLayout from "@/components/layout/PageLayout";
import { BROKERS } from "@/config/brokers";
import {
  ArrowLeftRight,
  ArrowRight,
  ArrowUpRight,
  Landmark,
  Play,
  Radar,
  ShieldCheck,
  Volume2,
  VolumeX,
} from "lucide-react";

// Flip to the live store URL once the app is public on Google Play.
const PLAY_STORE_URL: string | null = null;

const HIGHLIGHTS = [
  {
    title: "Screeners & scanners",
    description: "Buying pressure, market depth, volume spikes and RSI across NSE and BSE.",
    href: "/filtered-tick-analysis",
    icon: <Radar className="h-5 w-5" />,
  },
  {
    title: "Surveillance desk",
    description: "ESM, ASM and GSM lists — entries, stages and exits, tracked daily.",
    href: "/esm-stock-analysis",
    icon: <ShieldCheck className="h-5 w-5" />,
  },
  {
    title: "Follow the money",
    description: "Bulk, block, insider and SAST deals, plus big buying near the lows.",
    href: "/deals",
    icon: <ArrowLeftRight className="h-5 w-5" />,
  },
  {
    title: "Broker platform",
    description: "Connect your brokers and schedule orders to fire at the second you pick.",
    href: "/broker-platform",
    icon: <Landmark className="h-5 w-5" />,
  },
];

function PromoVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
    if (video.paused) video.play();
  };

  return (
    <div className="relative overflow-hidden rounded-xl border border-zinc-300 bg-zinc-950 dark:border-zinc-800">
      <video
        ref={videoRef}
        className="aspect-video w-full"
        src="/brand/draamview-promo.mp4"
        poster="/brand/draamview-promo-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-label="DraamView app walkthrough: scheduled orders, eight brokers in one desk, live watchlists and circuit alerts"
        onPlay={() => setIsPaused(false)}
        onPause={() => setIsPaused(true)}
      />
      {isPaused && (
        <button
          onClick={() => videoRef.current?.play()}
          aria-label="Play video"
          className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/20"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-zinc-900">
            <Play className="ml-0.5 h-6 w-6 fill-current" />
          </span>
        </button>
      )}
      <button
        onClick={toggleSound}
        aria-label={isMuted ? "Turn sound on" : "Turn sound off"}
        className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur transition-colors hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
      >
        {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </button>
    </div>
  );
}

function PlayStoreBadge() {
  const content = (
    <>
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
        <path fill="currentColor" d="M3.6 1.8 13.9 12 3.6 22.2c-.4-.2-.6-.6-.6-1.1V2.9c0-.5.2-.9.6-1.1Zm11.4 11.3 2.6 2.6-11.8 6.8 9.2-9.4Zm0-2.2L5.8 1.5l11.8 6.8-2.6 2.6Zm3.7-1.9 3 1.7c.8.5.8 1.6 0 2.1l-3 1.7-2.8-2.8 2.8-2.7Z" />
      </svg>
      <span className="flex flex-col leading-tight text-left">
        <span className="text-[10px] uppercase tracking-wider opacity-70">
          {PLAY_STORE_URL ? "Get it on" : "Coming soon on"}
        </span>
        <span className="text-sm font-semibold">Google Play</span>
      </span>
    </>
  );

  const badgeClasses =
    "inline-flex items-center gap-2.5 rounded-md border border-zinc-300 px-4 py-2 text-zinc-700 dark:border-zinc-700 dark:text-zinc-200";

  if (!PLAY_STORE_URL) {
    return (
      <span className={`${badgeClasses} cursor-default opacity-70`} aria-disabled="true">
        {content}
      </span>
    );
  }

  return (
    <a
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`${badgeClasses} transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800`}
    >
      {content}
    </a>
  );
}

function Hero() {
  const brokerNames = Object.values(BROKERS).map((broker) => broker.displayName);

  return (
    <section className="relative overflow-hidden">
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 md:py-20 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
            <span className="h-px w-8 bg-zinc-400 dark:bg-zinc-600" />
            Small &amp; microcap intelligence — NSE + BSE
          </p>
          <h1 className="mt-6 text-5xl text-zinc-900 sm:text-6xl xl:text-7xl dark:text-white">
            Spot the build-up.
            <br />
            <em className="text-emerald-700 dark:text-emerald-400">Place orders on time.</em>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-600 md:text-lg dark:text-zinc-400">
            DraamView tracks unusual volume, surveillance lists and big deals across both
            exchanges, then sends your scheduled orders to the brokers you already use.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              href="/tools"
              className="group inline-flex items-center gap-2 rounded-md bg-zinc-900 px-5 py-3 text-sm font-semibold text-zinc-50 transition-colors hover:bg-emerald-700 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-emerald-300"
            >
              Open the tools
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <PlayStoreBadge />
          </div>

          <div className="mt-10 border-t border-zinc-200 pt-5 dark:border-zinc-800">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
              {brokerNames.length} brokers, one desk
            </p>
            <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-zinc-700 dark:text-zinc-300">
              {brokerNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          <PromoVideo />
        </div>
      </div>
    </section>
  );
}

function Highlights() {
  return (
    <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 md:pb-24 lg:px-8">
      <div className="flex items-end justify-between gap-4 border-t border-zinc-900 pt-6 dark:border-zinc-100">
        <div>
          <h2 className="text-3xl text-zinc-900 md:text-4xl dark:text-white">
            Everything on one desk
          </h2>
          <p className="mt-1 text-sm text-zinc-500 md:text-base dark:text-zinc-400">
            Research, surveillance and order scheduling for the patient investor.
          </p>
        </div>
        <Link
          href="/tools"
          className="hidden shrink-0 items-center gap-1 text-sm font-semibold text-emerald-600 hover:underline sm:inline-flex dark:text-emerald-400"
        >
          All tools
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-8 grid border-l border-t border-zinc-200 sm:grid-cols-2 lg:grid-cols-4 dark:border-zinc-800">
        {HIGHLIGHTS.map((highlight, index) => (
          <Link
            key={highlight.href}
            href={highlight.href}
            className="group flex flex-col border-b border-r border-zinc-200 p-6 transition-colors hover:bg-white dark:border-zinc-800 dark:hover:bg-zinc-900"
          >
            <span className="flex items-center justify-between text-zinc-400 dark:text-zinc-500">
              <span className="font-mono text-xs">{String(index + 1).padStart(2, "0")}</span>
              <span className="text-emerald-700 dark:text-emerald-400">{highlight.icon}</span>
            </span>
            <span className="mt-10 flex items-center justify-between gap-2 font-display text-2xl text-zinc-900 dark:text-white">
              {highlight.title}
              <ArrowUpRight className="h-4 w-4 text-zinc-300 transition-colors group-hover:text-emerald-500 dark:text-zinc-600" />
            </span>
            <span className="mt-1 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
              {highlight.description}
            </span>
          </Link>
        ))}
      </div>

      <Link
        href="/tools"
        className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:underline sm:hidden dark:text-emerald-400"
      >
        All tools
        <ArrowUpRight className="h-4 w-4" />
      </Link>
    </section>
  );
}

export default function HomePage() {
  return (
    <PageLayout>
      <Hero />
      <Highlights />
    </PageLayout>
  );
}
